import { ActionWarning, GateActions, ReviewGate } from '../../services/caseWorkflowService';

/**
 * Words for the project page's business view: decisions waiting, work ready for
 * the next flow, and what decisions decided.
 *
 * Gate, option and queue labels come from the contract (reviewGates, workQueues);
 * only the page's own phrasing lives here.
 */

/** The window the outcomes strip reads, matching the workflow cards' run window. */
export const OUTCOME_WINDOW_DAYS = 30;

export const DECISIONS_CAPTION =
    'Cases waiting on a person at each review gate. Each case counts once, by its newest review.';

export const PIPELINE_CAPTION =
    'Workflows in the order cases move between them, with the decisions and queued work on each step. Click a decision to review its cases, or a workflow to open it.';

/** A queue's count on an arrow says what the cases are waiting for, briefly. */
const QUEUE_SHORT: Record<string, string> = {
    READY_FOR_TRIAGE: 'awaiting triage',
    READY_FOR_ENRICHMENT: 'ready for enrichment',
    READY_FOR_EDD: 'ready for EDD',
    READY_FOR_SAR: 'ready for SAR',
    HELD_FOR_EDD: 'held for EDD sign-off'
};

export const queueShort = (code: string, contractLabel?: string): string =>
    QUEUE_SHORT[code] ?? (contractLabel ? contractLabel.toLowerCase() : code.replace(/_/g, ' ').toLowerCase());

export const QUEUES_CAPTION =
    'Cases the platform has queued for the next workflow. They move on when that workflow runs.';

export const OUTCOMES_CAPTION = (days: number) =>
    `What the review gates decided in the last ${days} days, and who settled each decision. Counted per review decision, re-runs included, so these need not match the case counts above.`;

/** "3 cases" */
export const casesWord = (n: number): string => `${n.toLocaleString()} ${n === 1 ? 'case' : 'cases'}`;

/** What one warning means, and what to do about it. */
export const WARNING_TEXT: Record<string, (n: number) => { title: string; detail: string }> = {
    DRAFT_QUEUED_WHILE_EDD_OPEN: (n) => ({
        title: `${casesWord(n)} with a SAR draft queued while EDD is open`,
        detail: 'SAR preparation could pick these up before the EDD sign-off decides whether a SAR is warranted. Hold the drafts until the sign-off.'
    }),
    SIGN_OFF_WITHOUT_EDD_RECORD: (n) => ({
        title: `${casesWord(n)} awaiting EDD sign-off with no EDD case record`,
        detail: 'These EDD runs predate case tracking, so signing them off neither closes the EDD nor releases or withdraws the SAR draft. Re-run them through the EDD referral.'
    }),
    EDD_WITHOUT_CASE: (n) => ({
        title: `${casesWord(n)} whose EDD is not linked to the case`,
        detail: 'The EDD record carries no case id although the customer has a case, so the case reads as two cases in Case operations.'
    })
};

export const warningText = (warning: ActionWarning): { title: string; detail: string } =>
    (WARNING_TEXT[warning.code] ?? ((n: number) => ({ title: `${casesWord(n)}: ${warning.code}`, detail: '' })))(warning.count);

/**
 * A gate's suggestions, most common first, labelled from the contract.
 *
 * A suggestion the contract does not list is shown as it was recorded: EDD
 * reviews written before dispositions existed suggested a rating (LOW-CRITICAL),
 * and those threads are still waiting.
 */
export const suggestionsOf = (gate: GateActions, contractGate?: ReviewGate): { code: string; label: string; count: number; refersSar: boolean }[] => {
    const options = new Map((contractGate?.options ?? []).map((option) => [option.code, option]));
    return Object.entries(gate.bySuggestion)
        .map(([code, n]) => ({
            code,
            label: options.get(code)?.label ?? code,
            count: n,
            refersSar: Boolean(options.get(code)?.refersSar)
        }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
};

/** The transitions the outcomes strip draws, in the order a case meets them. */
export const OUTCOME_TRANSITIONS = [
    'ALERT_QUEUED',
    'ALERT_PRIORITISED',
    'REPORT_AUTO_CLOSED',
    'CONTEXT_ESCALATED',
    'EDD_REFERRED',
    'EDD_SAR_REFERRED',
    'EDD_COMPLETED',
    'REPORT_WITHDRAWN_AFTER_EDD'
];
